import type { MilestoneView } from "../../shared/types/api";

/**
 * How near a milestone is, and whether it was met.
 *
 * <p>Kept apart from the component so the arithmetic can be tested without rendering anything.
 * Every figure here is read off a plain date — the server sends days, not instants, and a
 * countdown that disagrees with the calendar on the wall is worse than no countdown.
 */

/** Today, as the server writes dates. Injected everywhere below so tests are not clock-dependent. */
export function today(): string {
  const now = new Date();
  return [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, "0"),
    String(now.getDate()).padStart(2, "0"),
  ].join("-");
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Whole days from {@code from} until {@code date}; negative once the date has passed.
 *
 * <p>Both sides are parsed as UTC midnight rather than local time. A local parse makes the
 * difference between two dates depend on whether a daylight-saving boundary falls between them,
 * which shows up as a countdown that is a day out for half the year.
 */
export function daysUntil(date: string, from: string): number {
  return Math.round((Date.parse(`${date}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / MS_PER_DAY);
}

/** The countdown as a person would say it. */
export function formatCountdown(days: number): string {
  if (days === 0) {
    return "today";
  }
  if (days === 1) {
    return "tomorrow";
  }
  if (days > 0) {
    return `in ${days} days`;
  }
  const late = Math.abs(days);
  return late === 1 ? "1 day overdue" : `${late} days overdue`;
}

export type CountdownTone = "calm" | "near" | "overdue";

/**
 * How loudly a countdown should be shown.
 *
 * <p>A week is the threshold because it is the last point at which a date can still be saved:
 * inside it, missing the milestone is a decision somebody is making rather than a surprise.
 */
export function countdownTone(days: number): CountdownTone {
  if (days < 0) {
    return "overdue";
  }
  return days <= 7 ? "near" : "calm";
}

export interface Achievement {
  text: string;
  tone: "good" | "bad";
}

/**
 * What reaching the milestone is worth saying, measured against what was committed.
 *
 * <p>Against the baseline date and nothing else. Measuring against the current plan would let a
 * milestone be re-planned onto the day it was reached and then reported as on time, which is the
 * re-planning §105 exists to make visible.
 *
 * @return null when there is nothing honest to say — not reached, or never baselined
 */
export function achievementNote(milestone: MilestoneView): Achievement | null {
  if (!milestone.actualDate || !milestone.baselineDate) {
    return null;
  }
  const drift = daysUntil(milestone.actualDate, milestone.baselineDate);
  if (drift === 0) {
    return { text: "on time", tone: "good" };
  }
  if (drift < 0) {
    const early = Math.abs(drift);
    return { text: `${early} ${early === 1 ? "day" : "days"} early`, tone: "good" };
  }
  return { text: `${drift} ${drift === 1 ? "day" : "days"} late`, tone: "bad" };
}

/** The date a milestone actually sits on: where it landed, or where it is headed. */
function positionOf(milestone: MilestoneView): string {
  return milestone.actualDate ?? milestone.forecastDate ?? milestone.currentDate;
}

/** Oldest first, so a project reads top to bottom as the sequence it is. */
export function orderMilestones(milestones: MilestoneView[]): MilestoneView[] {
  return [...milestones].sort((a, b) => positionOf(a).localeCompare(positionOf(b)));
}

export interface Journey {
  reached: number;
  /** Everything still on the journey — cancelled milestones are not counted against anyone. */
  total: number;
  percent: number;
  /** How many were reached on or before the date committed to. */
  onTime: number;
  /** The next date the project is driving towards, overdue ones first. */
  next: MilestoneView | null;
}

/**
 * Where the project has got to, as one line of arithmetic the header can read from.
 *
 * <p>Takes no "today": every figure here is about what was committed and what was reached, and
 * none of it changes because the clock moved. The countdown is the part that depends on today,
 * and that is computed per milestone where it is displayed.
 */
export function journey(milestones: MilestoneView[]): Journey {
  const counted = milestones.filter((each) => each.status !== "CANCELLED");
  const reached = counted.filter((each) => each.status === "ACHIEVED");
  const outstanding = orderMilestones(
    counted.filter((each) => each.status !== "ACHIEVED"),
  );

  return {
    reached: reached.length,
    total: counted.length,
    percent: counted.length === 0 ? 0 : Math.round((reached.length / counted.length) * 100),
    onTime: reached.filter((each) => achievementNote(each)?.tone === "good").length,
    // The soonest outstanding date, which is the overdue one when there is one: an overdue
    // milestone is not behind the project, it is in front of it.
    next: outstanding[0] ?? null,
  };
}
